import React, { useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * Chip-list editor with Enter-to-add + click-x-to-remove.
 *
 *   <ChipListEditor
 *     value={["a","b"]}
 *     onChange={(arr) => …}
 *     placeholder="Type and press Enter…"
 *     testId="brand-key-messages"
 *     disabled={false}
 *     minHeight="min-h-[44px]"   // optional, generous default
 *   />
 *
 * Used everywhere Phase 7 needs multi-value text input (brand_values, key_messages,
 * differentiators, proof_points, USPs, jobs_to_be_done, objections, audience_pains,
 * audience_desires, audience_expressions, etc.).
 */
export function ChipListEditor({
  value = [],
  onChange,
  placeholder = "Type and press Enter…",
  testId,
  disabled = false,
  minHeight = "min-h-[44px]",
  chipTone = "indigo",
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const safe = Array.isArray(value) ? value : [];

  const add = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (safe.includes(v)) {
      setDraft("");
      return;
    }
    onChange?.([...safe, v]);
    setDraft("");
  };

  const remove = (i) => onChange?.(safe.filter((_, idx) => idx !== i));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && safe.length > 0) {
      remove(safe.length - 1);
    } else if (e.key === ",") {
      e.preventDefault();
      add(draft);
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (text.includes(",") || text.includes("\n")) {
      e.preventDefault();
      const parts = text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
      const next = Array.from(new Set([...safe, ...parts]));
      onChange?.(next);
    }
  };

  const tones = {
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    slate:  "border-slate-200 bg-slate-50 text-slate-700",
    amber:  "border-amber-200 bg-amber-50 text-amber-900",
  };

  return (
    <div
      data-testid={testId}
      onClick={() => !disabled && inputRef.current?.focus()}
      className={`flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 ${minHeight} focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-text"}`}
    >
      {safe.map((v, i) => (
        <span
          key={`${v}-${i}`}
          data-testid={testId ? `${testId}-chip-${i}` : undefined}
          className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${tones[chipTone] || tones.indigo}`}
        >
          <span className="truncate" title={v}>{v}</span>
          {!disabled && (
            <button
              type="button"
              data-testid={testId ? `${testId}-remove-${i}` : undefined}
              onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-black/10"
              aria-label="remove"
            >
              <X className="h-3 w-3" strokeWidth={2} />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => add(draft)}
        placeholder={safe.length === 0 ? placeholder : ""}
        data-testid={testId ? `${testId}-input` : undefined}
        className="min-w-[120px] flex-1 border-0 bg-transparent p-1 text-sm text-slate-900 outline-none placeholder:text-slate-400"
      />
    </div>
  );
}
